import { Button } from "@/components/ui/button";
import { Pause, Play, X, SkipForward, Volume2, VolumeX, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatTime, modeById } from "@/lib/roninBox";
import { useRoninRunner } from "@/components/useRoninRunner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const frameSrc = (path) => (path?.startsWith("http") ? path : `${API_BASE}${path}`);

/** Flip-book player for a punch-cue animation. Restarts when its `key` changes (new cue). */
function CueFlipbook({ anim }) {
  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);
  const frames = anim?.frames || [];
  const frameMs = Math.max(60, anim?.frame_ms || 500);
  const loop = anim?.loop !== false;

  useEffect(() => {
    idxRef.current = 0;
    setIdx(0);
    if (frames.length < 2) return;
    const id = setInterval(() => {
      const next = idxRef.current + 1;
      if (next >= frames.length) {
        if (!loop) { clearInterval(id); return; }
        idxRef.current = 0;
      } else { idxRef.current = next; }
      setIdx(idxRef.current);
    }, frameMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (frames.length === 0) return null;
  return (
    <img
      src={frameSrc(frames[Math.min(idx, frames.length - 1)])}
      alt=""
      crossOrigin="anonymous"
      className="h-28 sm:h-44 w-auto object-contain"
      data-testid="ronin-cue-animation"
    />
  );
}

/**
 * Workout runner — presentational shell. All engine logic (timer loop, cue firing, phase
 * machine, audio, wake-lock, pause/skip/stop) lives in the `useRoninRunner` hook.
 * Props: { config: {mode, rounds, roundSecs, restSecs, intensity}, onExit, onComplete }
 */
export function RoninBoxRunner({ config, selectedCueIds, selectedLevel, onExit, onComplete }) {
  const { mode, rounds } = config;
  const {
    bootstrapped, bootError,
    phase, phaseSecs, roundIdx, paused, muted, cue, punches,
    activeAnim, animKey, exercise,
    activeMsRef,
    setMuted, togglePause, skipPhase, stop,
  } = useRoninRunner({ config, selectedCueIds, selectedLevel, onComplete });

  const total = phase === "round" ? config.roundSecs : phase === "rest" ? config.restSecs : 5;
  const pct = Math.max(0, Math.min(100, (phaseSecs / total) * 100));
  const modeMeta = modeById(mode);
  const phaseColor = phase === "rest" ? "#22c55e" : phase === "round" ? "#FF331F" : "#f59e0b";

  // Loading / error state — before cues + audio are ready
  if (!bootstrapped) {
    return (
      <div className="fixed inset-0 z-[110] bg-black text-white flex flex-col items-center justify-center p-6" data-testid="ronin-runner-loading">
        {bootError ? (
          <>
            <p className="text-[#FF331F] uppercase tracking-widest text-xs font-bold mb-3">Couldn&apos;t load</p>
            <p className="text-zinc-300 text-center max-w-md">{bootError}</p>
            <Button onClick={onExit} className="mt-6 bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs">Back</Button>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 text-[#FF331F] animate-spin mb-4" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Loading trainer voice</p>
            <p className="text-zinc-400 text-sm">Pre-loading audio cues…</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black text-white flex flex-col" data-testid="ronin-runner">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-[#27272A]">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box · {modeMeta.label}</p>
          <p className="text-xs text-zinc-500 mt-0.5">Round {Math.min(roundIdx + 1, rounds)} of {rounds}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setMuted((m) => !m)} className="h-9 w-9 text-zinc-400 hover:text-white" data-testid="ronin-mute">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={stop} className="h-9 w-9 text-zinc-400 hover:text-[#FF331F]" data-testid="ronin-stop">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Main display */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 relative overflow-hidden">
        {/* Background pulse on round phase */}
        {phase === "round" && (
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ background: `radial-gradient(circle at 50% 50%, ${phaseColor} 0%, transparent 70%)` }} />
        )}

        {/* Phase badge */}
        <div
          className="mb-6 sm:mb-10 px-5 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.3em] font-bold"
          style={{ background: phase === "rest" ? "#22c55e" : phase === "round" ? "#FF331F" : "#f59e0b", color: "#000" }}
          data-testid="ronin-phase"
        >
          {phase === "countdown" ? "Get ready" : phase === "round" ? "Box" : phase === "rest" ? "Rest" : "Done"}
        </div>

        {/* Timer */}
        <div className="font-display text-[clamp(72px,18vw,180px)] leading-none tracking-tighter text-white" data-testid="ronin-timer">
          {phase === "countdown" ? phaseSecs : formatTime(phaseSecs)}
        </div>

        {/* Cue text */}
        <div className="mt-6 sm:mt-10 min-h-[80px] sm:min-h-[120px] flex flex-col items-center justify-center gap-3">
          {activeAnim && <CueFlipbook key={animKey} anim={activeAnim} />}
          {exercise && (
            <div className="text-[11px] sm:text-sm uppercase tracking-[0.4em] font-black text-amber-400" data-testid="ronin-exercise-label">
              ● Exercise ●
            </div>
          )}
          <div className="font-display text-3xl sm:text-5xl md:text-6xl uppercase tracking-tight text-center px-4 transition-opacity duration-150" style={{ color: exercise ? "#FBBF24" : phaseColor }} data-testid="ronin-cue">
            {cue}
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-8 sm:mt-12 grid grid-cols-3 gap-6 sm:gap-12 text-center w-full max-w-md">
          <Stat label="Round" value={`${Math.min(roundIdx + 1, rounds)}/${rounds}`} />
          <Stat label="Punches" value={punches} />
          <Stat label="Active" value={formatTime(Math.round(activeMsRef.current / 1000))} />
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-[#1a1a1a]">
        <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, background: phaseColor }} />
      </div>

      {/* Bottom controls */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#27272A] border-t border-[#27272A]">
        <button
          onClick={togglePause}
          className="bg-[#0A0A0A] hover:bg-[#1a1a1a] py-5 flex items-center justify-center gap-2 text-sm uppercase tracking-widest font-bold transition-colors"
          data-testid="ronin-pause"
        >
          {paused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}
        </button>
        <button
          onClick={skipPhase}
          className="bg-[#0A0A0A] hover:bg-[#1a1a1a] py-5 flex items-center justify-center gap-2 text-sm uppercase tracking-widest font-bold transition-colors"
          data-testid="ronin-skip"
        >
          <SkipForward className="h-4 w-4" /> Skip {phase === "rest" ? "rest" : phase === "round" ? "round" : "intro"}
        </button>
        <button
          onClick={stop}
          className="hidden sm:flex bg-[#0A0A0A] hover:bg-[#FF331F] hover:text-white py-5 items-center justify-center gap-2 text-sm uppercase tracking-widest font-bold text-zinc-400 transition-colors"
          data-testid="ronin-end"
        >
          <X className="h-4 w-4" /> End workout
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl sm:text-3xl text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">{label}</div>
    </div>
  );
}
